import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	ProjectConfiguration,
	ProjectSubType,
} from '../models/projectConfiguration';

interface ProjectConfigurationState {
	configuration: ProjectConfiguration;
	configurations: ProjectConfiguration[];
	loading: boolean;
	error: string | null;
	projectSubTypes: ProjectSubType[];
}

const initialState: ProjectConfigurationState = {
	configuration: {
		id: '',
		created: '',
		updated: '',
		name: '',
		nameId: '',
		description: '',
		folders: [],
		company: '',
		templates: [],
		templateIds: [],
		projectDataCategoryIds: [],
		dataCategoryLength: 0,
		foldersLength: 0,
		templatesLength: 0,
	},
	configurations: [],
	loading: false,
	error: null,
	projectSubTypes: [],
};

const projectConfigurationSlice = createSlice({
	name: 'projectConfiguration',
	initialState,
	reducers: {
		setConfigurations(
			state,
			action: PayloadAction<ProjectConfiguration[]>
		) {
			state.configurations = action.payload;
		},
		addConfiguration(state, action: PayloadAction<ProjectConfiguration>) {
			state.configurations.push(action.payload);
		},
		updateConfiguration(
			state,
			action: PayloadAction<ProjectConfiguration>
		) {
			const idx = state.configurations.findIndex(
				(c) => c.id === action.payload.id
			);
			if (idx !== -1) state.configurations[idx] = action.payload;
		},
		setConfiguration(state, action: PayloadAction<ProjectConfiguration>) {
			state.configuration = action.payload;
		},
		setLoading(state, action: PayloadAction<boolean>) {
			state.loading = action.payload;
		},
		setError(state, action: PayloadAction<string | null>) {
			state.error = action.payload;
		},
		setProjectSubTypes(state, action: PayloadAction<ProjectSubType[]>) {
			state.projectSubTypes = action.payload;
		},
		updateFolderInConfiguration(
			state,
			action: PayloadAction<{
				folderId: string;
				updates: Partial<{
					display: string;
					active: boolean;
				}>;
			}>
		) {
			const { folderId, updates } = action.payload;

			// Recursive function to find and update folder
			const updateFolderRecursively = (folders: any[]): boolean => {
				for (const folder of folders) {
					if (folder.id === folderId) {
						Object.assign(folder, updates);
						return true;
					}
					if (folder.subFolders && folder.subFolders.length > 0) {
						if (updateFolderRecursively(folder.subFolders)) {
							return true;
						}
					}
				}
				return false;
			};

			// Update the folder in the current configuration
			if (state.configuration.folders) {
				updateFolderRecursively(state.configuration.folders);
			}
		},
	},
});

export const {
	setConfigurations,
	addConfiguration,
	updateConfiguration,
	setConfiguration,
	setLoading,
	setError,
	setProjectSubTypes,
	updateFolderInConfiguration,
} = projectConfigurationSlice.actions;

export default projectConfigurationSlice.reducer;
